/** @format */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  CreateImageJobPayload,
  createImageJob,
  getImageJob,
} from '../../../api/imageGeneration.api';
import { parseError } from '../../../helpers';

/**
 * Drives an async AI image-generation job:
 * - createJob() queues the job and stores its id
 * - the job is then polled until it COMPLETEs or FAILs
 */
export const useAIImageGen = () => {
  const [jobId, setJobId] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (payload: CreateImageJobPayload) => createImageJob(payload),
    onSuccess: (job) => setJobId(job.id),
    onError: (error) => toast.error(parseError(error)),
  });

  const jobQuery = useQuery({
    queryKey: ['image-generation-job', jobId],
    queryFn: () => getImageJob(jobId as string),
    enabled: !!jobId,
    // Poll while the job is still being worked on; stop once it settles.
    refetchInterval: (data) =>
      data && (data.status === 'PENDING' || data.status === 'PROCESSING')
        ? 2500
        : false,
  });

  const job = jobQuery.data ?? null;
  const isGenerating =
    createMutation.isLoading ||
    (!!job && (job.status === 'PENDING' || job.status === 'PROCESSING'));

  const reset = () => {
    setJobId(null);
    createMutation.reset();
  };

  return {
    createJob: createMutation.mutate,
    job,
    isGenerating,
    reset,
    images: job?.status === 'COMPLETED' ? job.resultUrls : [],
    failed: job?.status === 'FAILED',
    errorMessage: job?.error ?? null,
  };
};
